import { BRAND_NAME } from "../../constants/brand";
import { Grid, InputAdornment, CircularProgress, Divider } from "@mui/material"
import styles from "../../Styles/ComponentStyles/Quoter/RothQuoter.module.scss"
import calculatorIcon from "../../assets/Images/calculatorIcon.svg"
import GlobalTextfield from "../../GlobalComponent/GlobalTextfield/GlobalTextfield"
import { useState, useRef, useEffect } from "react"
import GlobalButton from "../../Utils/GlobalUtilsComponents/GlobalButton"
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import agent from "../../service/api"
import { useSnackbar } from "../../GlobalComponent/GlobalSnackbar/GlobalSnackbar"
import IconsColor from "../../Utils/IconsColor"
import newRBLogoForWhiteBg from "../../assets/Images/mar-lockup.svg"

const RothQuoter = () => {
    useEffect(() => {
        document.title = "Roth Conversion Quoter"
    }, [])


    const [formData, setFormData] = useState({
        currentAge: "",
        retirementAge: "",
        conversionAmount: "",
        currentTaxBracket: "",
        anticipatedTaxBracket: "",
        expectedRateOfReturn: "",
    })

    const [calcResult, setCalcResult] = useState({
        "netRothIraValue": "",
        "netTraditionalIraValue": "",
        "potentialTaxSavings": ""
    })

    const [isCalculated, setIsCalculated] = useState(false)
    const [flagLoader, setFlagLoader] = useState(false)
    const resultsRef = useRef<HTMLDivElement>(null)

    const { showSnackbar } = useSnackbar()

    // Add this check to determine if all fields are filled
    const isFormComplete = () => {
        return (
            formData.currentAge !== "" &&
            formData.retirementAge !== "" &&
            formData.conversionAmount !== "" &&
            formData.currentTaxBracket !== "" &&
            formData.anticipatedTaxBracket !== "" &&
            formData.expectedRateOfReturn !== ""
        )
    }

    // Add these helper functions after your state declarations

    // Format number with thousand separators
    const formatWithCommas = (value: string) => {
        const number = value.replace(/,/g, '');
        if (number === '') return '';
        return Number(number).toLocaleString('en-US');
    }

    const handleCalculate = async () => {
        try {
            setFlagLoader(true)

            const response = await agent.rothConversionQuoterCalcAPI.post(formData);

            if (response?.status && response?.result) {
                setIsCalculated(true)
                setCalcResult(response?.result)

                // Wait for next frame to ensure DOM is updated
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (resultsRef.current) {
                            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    });
                });
            } else {
                showSnackbar("Something Went Wrong!", "error")
            }
        } catch (error) {
            console.error("Error calculating:", error);
        } finally {
            setFlagLoader(false)
        }
    }

    // Remove non-numeric characters (except decimal for percentage fields)
    const sanitizeNumberInput = (value: string, allowDecimal: boolean = false) => {
        if (allowDecimal) {
            // Allow only numbers and one decimal point
            let sanitized = value.replace(/[^\d.]/g, '');
            // Ensure only one decimal point
            const parts = sanitized.split('.');
            if (parts.length > 2) {
                sanitized = parts[0] + '.' + parts.slice(1).join('');
            }
            return sanitized;
        } else {
            // Allow only numbers
            return value.replace(/\D/g, '');
        }
    }

    // Update the handleInputChange function
    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let value = event.target.value;

        // Handle different field types
        switch (field) {
            case 'conversionAmount':
                // Remove $ and commas first
                value = value.replace(/[$,]/g, '');
                value = sanitizeNumberInput(value, false);
                break;

            case 'currentTaxBracket':
            case 'anticipatedTaxBracket':
            case 'expectedRateOfReturn':
                // Remove % first
                value = value.replace(/%/g, '');
                value = sanitizeNumberInput(value, true);
                // Limit to 100 for percentages
                if (parseFloat(value) > 100) {
                    value = '100';
                }
                break;

            case 'currentAge':
            case 'retirementAge':
                value = sanitizeNumberInput(value, false);
                break;

            default:
                break;
        }

        setFormData((prev: any) => ({
            ...prev,
            [field]: value
        }))
    }

    return (
        <>
            <Grid container className={styles.quoterMainContainer}>
                <Grid item xl={5} lg={8} md={9} sm={11} xs={11} className={styles.calculatorContainer}>
                    <Grid container gap={3}>
                        <Grid item xs={12}>
                            <Grid container gap={2} alignItems={"center"}>
                                <Grid item className={styles.calcIconItem}>
                                    <img src={calculatorIcon} alt="Icon" />
                                </Grid>
                                <Grid item xs>
                                    <Grid container>
                                        <Grid item xs={12} className={styles.heading}>
                                            Roth IRA Impact Estimator
                                        </Grid>
                                        <Grid item xs={12} className={styles.subHeading}>
                                            Estimate the tax impact of converting your traditional IRA to a Roth IRA and plan your retirement strategy more efficiently.
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} className={styles.inputSection}>
                            <Grid container gap={2} className={styles.leftSection}>
                                <Grid item xl={5.8} lg={5.8} md={5.8} sm={5.8} xs={11}>
                                    <Grid container gap={2}>
                                        <Grid item xs={12}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.inputLabel}>
                                                    Current Age
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        value={formData.currentAge}
                                                        onChange={handleInputChange("currentAge")}
                                                        variant="outlined"
                                                        fullWidth
                                                        stylingClassName={styles.textField}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.inputLabel}>
                                                    Retirement Age
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        value={formData.retirementAge}
                                                        onChange={handleInputChange("retirementAge")}
                                                        variant="outlined"
                                                        fullWidth
                                                        stylingClassName={styles.textField}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.inputLabel}>
                                                    Conversion Amount ($)
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        value={`${formatWithCommas(formData.conversionAmount)}`}
                                                        onChange={handleInputChange("conversionAmount")}
                                                        variant="outlined"
                                                        fullWidth
                                                        stylingClassName={styles.textField}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xl={5.8} lg={5.8} md={5.8} sm={5.8} xs={11}>
                                    <Grid container gap={2}>
                                        <Grid item xs={12}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.inputLabel}>
                                                    Current Tax Bracket (%)
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        value={`${formData.currentTaxBracket}`}
                                                        onChange={handleInputChange("currentTaxBracket")}
                                                        variant="outlined"
                                                        fullWidth
                                                        stylingClassName={styles.textField}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.inputLabel}>
                                                    Anticipated Tax Bracket (%)
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        value={`${formData.anticipatedTaxBracket}`}
                                                        onChange={handleInputChange("anticipatedTaxBracket")}
                                                        variant="outlined"
                                                        fullWidth
                                                        stylingClassName={styles.textField}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.inputLabel}>
                                                    Expected Rate of Return (%)
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        value={`${formData.expectedRateOfReturn}`}
                                                        onChange={handleInputChange("expectedRateOfReturn")}
                                                        variant="outlined"
                                                        fullWidth
                                                        stylingClassName={styles.textField}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} className={styles.calculateButtonContainer}>
                                    <GlobalButton
                                        onClick={handleCalculate}
                                        className={styles.calculateButton}
                                        variant="contained"
                                        fullWidth
                                        disabled={!isFormComplete() || flagLoader}
                                        value={"Calculate Potential Tax Savings"}
                                        endIcon={flagLoader ? <CircularProgress size={16} color="inherit" /> : undefined}
                                    />
                                    <div className={styles.assumptionTxt}>
                                        * This analysis assumes you'll pay the conversion tax using funds outside your Roth IRA, which is typically the most tax-efficient strategy.
                                    </div>
                                </Grid>
                            </Grid>
                        </Grid>
                        {
                            isCalculated &&
                            <>
                                <Grid xs={12} className={styles.rightSection} ref={resultsRef}>
                                    <Divider
                                        style={{
                                            opacity: 0.8,
                                            borderColor: "#D1D1D1",
                                            height: "1px",
                                            width: "100%",
                                            marginBottom: "20px"
                                        }}
                                    />
                                    <Grid container className={styles.taxSavingsCard}>
                                        <Grid item xs={12} justifyContent={"center"} display={"flex"}>
                                            <div className={styles.iconContainer}>
                                                <TrendingUpIcon style={{ color: "#3B78CE" }} />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} className={styles.cardTitle}>
                                            Potential Tax Savings
                                        </Grid>
                                        <Grid item xs={12} className={styles.cardAmount}>
                                            {calcResult?.potentialTaxSavings}
                                        </Grid>
                                        <Grid item xs={12} className={styles.cardDescription}>
                                            This represents your estimated long-term tax benefit from converting to a Roth IRA, accounting for tax-free growth and withdrawals in retirement.
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </>
                        }
                        <Grid item xs={12} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                            <span className={styles.poweredbyTxt}>Powered by</span>
                            <img style={{ marginLeft: "5px", width: "auto", height: "auto", maxHeight: "23px", maxWidth: "70%" }} src={newRBLogoForWhiteBg} alt={`${BRAND_NAME} Logo`} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid >
        </>
    )
}

export default RothQuoter